import React from 'react';
import {
    Dialog as AriaDialog,
    type DialogProps as AriaDialogProps,
    Heading,
    Modal,
    ModalOverlay,
    type ModalOverlayProps,
} from 'react-aria-components';
import { type FieldValues, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { classnames } from '@/utils/classnames.ts';

import Button from './Button.tsx';
import Form, { type FormProps } from './Form.tsx';

export interface DialogProps extends Pick<ModalOverlayProps, 'isOpen' | 'isDismissable' | 'isKeyboardDismissDisabled'> {
    onClose: () => void;
    // `xl` for a grid of cards, e.g. the providers to connect.
    size?: 'md' | 'lg' | 'xl';
    className?: string;
    children: AriaDialogProps['children'];
}

// Its content is mounted only while it's open, so a form in it starts from its default values at every opening.
const DialogRoot = ({ isDismissable = true, onClose, size = 'md', className, children, ...props }: DialogProps) => (
    <ModalOverlay
        {...props}
        isDismissable={isDismissable}
        onOpenChange={(isOpen) => !isOpen && onClose()}
        className="fixed inset-0 z-40 flex items-end justify-center bg-black/30 p-0 sm:items-center sm:p-6 dark:bg-black/60"
    >
        <Modal
            className={classnames(
                'w-full',
                size === 'md' && 'max-w-lg',
                size === 'lg' && 'max-w-2xl',
                size === 'xl' && 'max-w-4xl'
            )}
        >
            <AriaDialog
                className={classnames(
                    'flex max-h-[90dvh] flex-col overflow-auto rounded-t-card bg-surface p-6 shadow-xl outline-none sm:rounded-card',
                    className
                )}
            >
                {children}
            </AriaDialog>
        </Modal>
    </ModalOverlay>
);

const DialogTitle = ({ className, ...props }: React.ComponentPropsWithRef<'h2'>) => (
    <Heading slot="title" {...props} className={classnames('text-xl font-semibold tracking-tight', className)} />
);

const DialogBody = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div {...props} className={classnames('mt-3 flex flex-col gap-4', className)} />
);

const DialogDescription = ({ className, ...props }: React.ComponentPropsWithRef<'p'>) => (
    <p {...props} className={classnames('text-text-muted', className)} />
);

const DialogFooter = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div {...props} className={classnames('mt-6 flex justify-end gap-2', className)} />
);

export interface DialogFormProps<TFieldValues extends FieldValues, TTransformedValues> extends Omit<
    FormProps<TFieldValues, TTransformedValues>,
    'title'
> {
    title: React.ReactNode;
    description?: React.ReactNode;
    submitLabel: React.ReactNode;
    // Shown on the submit button while submitting, e.g. "Connecting…", with a spinner.
    pendingLabel?: React.ReactNode;
    submitVariant?: 'primary' | 'danger';
    isSubmitDisabled?: boolean;
    // The button next to submit: cancel by default, or e.g. back to the previous step.
    onCancel: () => void;
    cancelLabel?: React.ReactNode;
}

// A dialog's content as a form: the title, the fields (children) with the error of a failed submit, and the buttons.
const DialogForm = <TFieldValues extends FieldValues, TTransformedValues>({
    form,
    title,
    description,
    submitLabel,
    pendingLabel,
    submitVariant = 'primary',
    isSubmitDisabled = false,
    onCancel,
    cancelLabel,
    children,
    ...props
}: DialogFormProps<TFieldValues, TTransformedValues>) => {
    const { t } = useTranslation();
    const { isSubmitting } = useFormState({ control: form.control });

    return (
        <Form {...props} form={form}>
            <DialogTitle>{title}</DialogTitle>
            <DialogBody>
                {description && <DialogDescription>{description}</DialogDescription>}
                {children}
                <Form.Error />
            </DialogBody>
            <DialogFooter>
                <Button variant="ghost" onClick={onCancel}>
                    {cancelLabel ?? t('common.cancel')}
                </Button>
                <Button
                    type="submit"
                    variant={submitVariant}
                    isPending={isSubmitting && !!pendingLabel}
                    disabled={isSubmitting || isSubmitDisabled}
                >
                    {isSubmitting && pendingLabel ? pendingLabel : submitLabel}
                </Button>
            </DialogFooter>
        </Form>
    );
};

export default Object.assign(DialogRoot, {
    Title: DialogTitle,
    Body: DialogBody,
    Description: DialogDescription,
    Footer: DialogFooter,
    Form: DialogForm,
});
